import React, { useMemo, useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { useParcels } from './data/useParcels';
import { safeUrl } from './util/safeUrl';
import { LensId } from './data/lenses';
import { emptyFilters, applyFilters, filterCount, describeFilters, FilterState, CriteriaBar } from './filters/Filters';
import { facetFilters } from './panels/breakdownFilter';
import MapCanvas from './map/MapCanvas';
import LeftRail, { searchHaystack } from './panels/LeftRail';
import AnalyticsPanel from './panels/AnalyticsPanel';
// DetailPanel is code-split out of the initial bundle (it only renders once a lot is opened) to
// shrink the cold-load parse. It's prefetched on idle (effect below) so the first lot-open is
// instant — the Suspense fallback effectively never shows.
const DetailPanel = lazy(() => import('./panels/DetailPanel'));
import CommandPalette from './panels/CommandPalette';
import { fmt, lensTokens, type MeasureKey } from './data/aggregate';
import { injectBaseCss, applyTheme, setThemeTokens, T } from './theme/tokens';
import { useExportData } from './export/useExportData';
import { splitPrefKey, loadSplits, saveSplits, safeStore } from './data/splitPref';
import { getBlockIdBestEffort } from './panels/dock/dockViews';
import { LENSES, cbState, lensLabel, ensureLensPalette } from './data/lenses';
import Dock from './panels/dock/Dock';
import { Btn } from './ui/primitives';

const BUILD = 'IFX157-jobfix'; // interface-extension build — bump to confirm the latest bundle loaded

const lsGet = (k: string) => { try { return localStorage.getItem('lfe.' + k); } catch { return null; } };
const lsSet = (k: string, v: string) => { try { localStorage.setItem('lfe.' + k, v); } catch { /* sandboxed */ } };
const readLens = (): LensId => { const v = lsGet('lens'); return v && (v in LENSES) ? v as LensId : 'landUse'; };
const readBool = (k: string, d: boolean) => { const v = lsGet(k); return v == null ? d : v === '1'; };

// ---- Responsive auto-density ------------------------------------------------------------------
// The layout is designed for a ~1,600px-wide viewport. Below that (Eric's display at 100% browser
// zoom is ~1,000–1,300 effective px) the chrome crams: the toolbar wraps to two rows, dock KPI
// cards stack, the detail title wraps, and the map shrinks to a sliver — while 67% browser zoom
// looks right. So we scale the CHROME automatically with the CSS `zoom` property (which reflows
// layout, unlike transform:scale — no transform fallback on purpose). Supported in all modern
// Chromium/Safari and Firefox ≥126; older Firefox simply ignores it and keeps today's unscaled look.
// CRITICAL: the Leaflet map container is NEVER zoomed — Leaflet's pointer/coordinate math needs a
// 1:1 container (browser zoom is uniform, CSS zoom on the map pane risks hit-test offsets).
const DESIGN_W = 1600;
const UI_SCALE_MIN = 0.67; // floor = the browser-zoom level verified to fit; never upscale past 1.
function useUiScale(): number {
  const calc = () => Math.min(1, Math.max(UI_SCALE_MIN, Math.round((window.innerWidth / DESIGN_W) * 100) / 100));
  const [scale, setScale] = useState(calc);
  useEffect(() => {
    let raf = 0;
    const onResize = () => { if (!raf) raf = requestAnimationFrame(() => { raf = 0; setScale(calc()); }); };
    window.addEventListener('resize', onResize);
    return () => { window.removeEventListener('resize', onResize); if (raf) cancelAnimationFrame(raf); };
  }, []);
  return scale;
}

export default function SiteMapApp() {
  useEffect(() => { injectBaseCss(); }, []);
  const { parcels, loading, permitRows, inspectionRows, unitRows, punchRows, lotUpdateRows, changeLogRows, lotIndex, inspCountByPermitNumber, fieldNames, missingFields, syncedAt } = useParcels();

  // As an interface extension the element is mounted into a container that Interface
  // Designer sizes (height set by the page layout), so we simply fill it 100%. No more
  // SDK-viewport / screen-height clamp — that was a base-extension iframe-balloon fix.

  const [lens, setLens] = useState<LensId>(readLens);
  // "Split by" — an ORDERED stack of further lenses (max MAX_SPLITS deep) — owned HERE (not
  // the panel) so the rail picker, the analytics panel's split tree, and the map hover tooltip
  // all speak one synced state. Persistence migrates the old single-lens pref transparently.
  const [splits, setSplitsState] = useState<LensId[]>(() => loadSplits(safeStore(), splitPrefKey('app', getBlockIdBestEffort()), readLens()));
  const setSplits = useCallback((s: LensId[]) => { setSplitsState(s); saveSplits(safeStore(), splitPrefKey('app', getBlockIdBestEffort()), s); }, []);
  // primary changing onto a split layer removes that layer; deeper layers promote up (filter keeps order)
  useEffect(() => { if (splits.includes(lens)) setSplits(splits.filter(s => s !== lens)); }, [lens, splits, setSplits]);
  // Units-grid column order (drag-to-reorder). Owned here so a saved view can capture + restore it
  // alongside lens/filters/splits; localStorage keeps the last order as the default between sessions.
  const COLORDER_KEY = 'lfe.unitsGrid.colOrder.v1';
  const [colOrder, setColOrderState] = useState<string[]>(() => { try { const v = JSON.parse(localStorage.getItem(COLORDER_KEY) || '[]'); return Array.isArray(v) ? v : []; } catch { return []; } });
  const setColOrder = useCallback((ks: string[]) => { setColOrderState(ks); try { localStorage.setItem(COLORDER_KEY, JSON.stringify(ks)); } catch { /* sandboxed */ } }, []);
  const [filters, setFilters] = useState<FilterState>(emptyFilters());
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [flyToId, setFlyToId] = useState<string | null>(null);
  const [flyNonce, setFlyNonce] = useState(0);
  const [fitNonce, setFitNonce] = useState(0);
  const [tilt, setTilt] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [isolated, setIsolated] = useState<Set<string>>(new Set());
  const [exporting, setExporting] = useState<string | null>(null);
  const [present, setPresent] = useState(false);
  const [cb, setCb] = useState(() => readBool('cb', false));
  const [dark, setDark] = useState(false); // button-controlled ONLY — no storage restore (was turning on "randomly")
  const [showMarkers, setShowMarkers] = useState(() => readBool('markers2', false)); // default OFF (key bumped markers→markers2 so the old default-on persisted value is ignored)
  const [hovered, setHovered] = useState<string | null>(null);            // analytics/legend CATEGORY
  const [hoveredPath, setHoveredPath] = useState<string[] | null>(null);  // split-tree sub-row → map intersection spotlight
  const [hoveredLotIds, setHoveredLotIds] = useState<string[] | null>(null); // precise id-set spotlight (panel-computed)
  const [pinnedLots, setPinnedLots] = useState<string[] | null>(null);    // breakdown sub-value CLICK → persistent map spotlight
  const [hoveredLotId, setHoveredLotId] = useState<string | null>(null); // dock/search ROW → map badge
  const [paletteOpen, setPaletteOpen] = useState(false);
  // Auto-density: spread into every CHROME wrapper (rail / detail panel / dock / map overlays) —
  // never the Leaflet container. A zoomed region's visual size = logical px × uiScale, so any px
  // that crosses the zoom boundary (grid column widths, pointer-drag deltas) is corrected below.
  const uiScale = useUiScale();
  const chromeZoom = useMemo<React.CSSProperties>(() => ({ zoom: uiScale }), [uiScale]);
  cbState.on = cb; // colour-blind-safe palette flag read by catColor() during this render
  setThemeTokens(dark); // sync swap during render → inline T.* colors correct this frame (no stale light-on-dark)
  useEffect(() => { applyTheme(dark); }, [dark]); // re-inject CSS for class-based styles
  // Warm the code-split DetailPanel chunk during idle (after the map paints) so the first lot-open
  // is instant instead of paying a chunk fetch + Suspense fallback. Best-effort; ignored if it fails.
  useEffect(() => {
    const ric = (globalThis as any).requestIdleCallback as undefined | ((cb: () => void, o?: any) => number);
    const warm = () => { import('./panels/DetailPanel').catch(() => { /* prefetch is best-effort */ }); };
    const id = ric ? ric(warm, { timeout: 2500 }) : (setTimeout(warm, 1200) as unknown as number);
    return () => { const cic = (globalThis as any).cancelIdleCallback as undefined | ((id: number) => void);
      if (ric && cic) cic(id); else clearTimeout(id as any); };
  }, []);
  // Remember personal preferences across sessions (localStorage, sandbox-safe).
  useEffect(() => { lsSet('lens', lens); }, [lens]);
  useEffect(() => { lsSet('cb', cb ? '1' : '0'); }, [cb]);
  useEffect(() => { lsSet('markers2', showMarkers ? '1' : '0'); }, [showMarkers]);
  const exportData = useExportData();
  // Active analytics measure (Lots/Units/SF/GPD/Value), reported up by the panel so the PDF
  // distribution mirrors what's on screen.
  const [exportMeasure, setExportMeasure] = useState<MeasureKey>('lots');
  // Seed the dynamic-lens colour registry from the FULL dataset (not a filtered view) so each
  // model/package/version category gets a stable, distinct colour that matches across the live
  // map, the analytics panel, and every export — even when the export is of a filtered subset.
  useEffect(() => {
    if (!parcels.length) return;
    for (const def of Object.values(LENSES)) ensureLensPalette(def, parcels.flatMap(p => lensTokens(def, p)));
  }, [parcels]);

  useEffect(() => { setIsolated(new Set()); setPinnedLots(null); }, [lens]); // isolation + pin are per-lens
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) { e.preventDefault(); setPaletteOpen(o => !o); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const filteredOut = useMemo(() => applyFilters(parcels, filters), [parcels, filters]);
  const visible = useMemo(() => parcels.filter(p => !filteredOut.has(p.id)), [parcels, filteredOut]);
  // Faceted-search set for the analytics breakdown: `visible` but with ONLY the PRIMARY colour-by
  // lens's own value-filter lifted — so its categories all stay visible in the right panel and you
  // can multi-select two or three by clicking (the field's filter ORs within its key) instead of
  // the list collapsing to the one you picked. SPLIT-lens filters are deliberately NOT lifted: a
  // split sub-row must respect every active filter, else (e.g. colour by Permit, split by Contract,
  // filter Contract→Escrow) the sub-rows show contract values you filtered OUT and the breakdown
  // Total stops matching the header. The map/dock/footer always narrow to the real `visible`
  // selection. When the colour lens isn't filtered this equals `visible`, so nothing changes.
  const facetParcels = useMemo(() => {
    const ff = facetFilters(filters, [lens]); // primary colour lens only — NOT the splits
    if (ff === filters) return visible; // nothing lifted → identical to the visible selection
    const out = applyFilters(parcels, ff);
    return parcels.filter(p => !out.has(p.id));
  }, [parcels, visible, filters, lens]);
  const selected = useMemo(() => parcels.find(p => p.id === selectedId) || null, [parcels, selectedId]);
  const selIndex = useMemo(() => visible.findIndex(p => p.id === selectedId), [visible, selectedId]);

  // Stable identities (only call setters) so React.memo'd children below don't re-render
  // when unrelated high-frequency state (hover, dock drag, export status) changes.
  const flyTo = useCallback((id: string) => { setFlyToId(id); setFlyNonce(n => n + 1); }, []);
  const select = useCallback((id: string) => { setSelectedId(id); setShowDetail(true); }, []);
  const selectAndFly = useCallback((id: string) => { select(id); flyTo(id); }, [select, flyTo]);
  // Map click toggles: clicking the already-selected lot deselects it and closes the detail panel.
  // (Dock/search/command use selectAndFly, which always selects.)
  const toggleSelect = useCallback((id: string) => {
    setSelectedId(prev => { if (prev === id) { setShowDetail(false); return null; } setShowDetail(true); return id; });
  }, []);
  // A lot found via the RAIL SEARCH scopes the toolbar Excel export to JUST that lot (Eric: "search a
  // lot → export only that lot"). Only the search box sets this — map clicks/dock/command don't — and
  // the export-time guard (`selectedId === searchedLotId`) means any later map-click, Esc, or dock
  // selection that moves off the searched lot automatically reverts the export to the full view.
  const [searchedLotId, setSearchedLotId] = useState<string | null>(null);
  const searchSelect = useCallback((id: string) => { selectAndFly(id); setSearchedLotId(id); }, [selectAndFly]);

  // ---- Bottom-dock management console: scope = the SAME `visible` lot set as the map ----
  const [dockOpen, setDockOpen] = useState(false);
  const toggleDock = useCallback(() => setDockOpen(o => !o), []);
  const [dockHeight, setDockHeight] = useState(460);
  const [detailWidth, setDetailWidth] = useState(() => { const v = lsGet('detailW'); const n = v ? parseInt(v, 10) : NaN; return isNaN(n) ? 332 : Math.min(720, Math.max(300, n)); });
  useEffect(() => { lsSet('detailW', String(detailWidth)); }, [detailWidth]);
  const visibleLotIdSet = useMemo(() => new Set(visible.map(p => p.id)), [visible]);
  // Unit rows scoped to the SAME visible lot set the map/KPIs use — the right-panel units grid's
  // full (un-drilled) scope. A unit belongs if its primary lot OR any of its lots is visible.
  const visibleUnitRows = useMemo(
    () => unitRows.filter(u => (u.lotId != null && visibleLotIdSet.has(u.lotId)) || u.lotIds.some(id => visibleLotIdSet.has(id))),
    [unitRows, visibleLotIdSet]);
  // LOT-GRAIN "active lot with no permit" — same definition + fields as the audited ground truth
  // (contract summary committed/pipeline OR build started; Permits link empty). The previous
  // unit-grain version over-counted 67 vs the verified 23.
  const activeNoPermit = useMemo(() => {
    // undefined until ANY permit link is visible — pre-permit-load every lot reads permitCount 0
    // and the KPI would flash a huge false gap.
    if (!visible.some(p => p.permitCount > 0)) return undefined;
    const ACTIVE = new Set(['Escrow', 'Under contract', 'Requested', 'Sent', 'Prospect']);
    const STARTED = new Set(['Shell', 'Rough', 'Finishes', 'Done']);
    return visible.filter(p => (ACTIVE.has(p.salesBucket) || STARTED.has(p.buildPhase)) && p.permitCount === 0).length;
  }, [visible]);
  const dockData = useMemo(() => ({ permitRows, inspectionRows, unitRows, punchRows, lotUpdateRows, changeLogRows, lotIndex, inspCountByPermitNumber, loading, syncedAt }),
    [permitRows, inspectionRows, unitRows, punchRows, lotUpdateRows, changeLogRows, lotIndex, inspCountByPermitNumber, loading, syncedAt]);
  // Active "Split by" stack as lens defs — the SAME effective stack the analytics panel renders
  // (primary + buildOrder excluded), so every export's split visualization matches the panel.
  const effSplits = useMemo(
    () => lens === 'buildOrder' ? [] : splits.filter(s => s !== lens && s !== 'buildOrder').map(s => LENSES[s]),
    [splits, lens]);
  // Map context for the dock's ↓PDF — page 1 of that export is the same designed map dashboard
  // as the toolbar's ⤓ PDF (filtered lots + legend + criteria provenance + split breakdown), so
  // the dock report's lot numbers correspond to a map the reader can see.
  const dockMapExport = useMemo(() => ({
    visible, all: parcels, lens: LENSES[lens], splits: effSplits,
    criteria: describeFilters(filters, fieldNames),
    scope: visible.length === parcels.length ? `all ${fmt(parcels.length)} lots` : `${fmt(visible.length)} of ${fmt(parcels.length)} lots (filtered)`,
    totalSF: visible.reduce((s, p) => s + (p.lotSizeSF || 0), 0),
  }), [visible, parcels, lens, effSplits, filters, fieldNames]);
  const gridCtx = useMemo(() => ({
    selectedLotId: selectedId,
    onSelectLot: (id: string) => selectAndFly(id),
    onHoverLot: (id: string | null) => setHoveredLotId(id),
    openUrl: (url: string) => { const u = safeUrl(url); if (u) window.open(u, '_blank', 'noopener'); },
    jumpToInspections: (_n: string) => {},  // overridden inside Dock
  }), [selectedId]); // eslint-disable-line
  const toggleIsolate = useCallback((cat: string) => {
    if (cat === '__clear__') return setIsolated(new Set());
    setIsolated(prev => { const n = new Set(prev); n.has(cat) ? n.delete(cat) : n.add(cat); return n; });
  }, []);

  function runSearch() {
    const q = query.trim().toLowerCase(); if (!q) return;
    const hit = parcels.find(p => searchHaystack(p).includes(q));
    if (hit) searchSelect(hit.id);
  }
  function nav(d: -1 | 1) {
    if (selIndex < 0 || !visible.length) return;
    selectAndFly(visible[(selIndex + d + visible.length) % visible.length].id);
  }
  // Lot navigation: ← / → step through visible lots when one is selected; Esc closes.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.key === 'Escape') { setSelectedId(null); setShowDetail(false); setPaletteOpen(false); }
      else if (selectedId && e.key === 'ArrowRight') { e.preventDefault(); nav(1); }
      else if (selectedId && e.key === 'ArrowLeft') { e.preventDefault(); nav(-1); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedId, visible]); // eslint-disable-line
  async function exportXLSX() {
    // Rail-search scope: if the currently-selected lot came from the search box, export ONLY that lot
    // (map still shows everything). Guard reverts to the full view the moment the selection moves.
    const searchScopeId = (searchedLotId && selectedId === searchedLotId) ? searchedLotId : null;
    const scopeParcels = searchScopeId ? parcels.filter(p => p.id === searchScopeId) : visible;
    const visibleLotIds = new Set(scopeParcels.map(p => p.id));
    const rows = exportData.buildRows(visibleLotIds);
    if (!rows.length) { setExporting(searchScopeId ? 'No units on the searched lot to export.' : 'No units in the current view to export.'); setTimeout(() => setExporting(null), 2600); return; }
    // Wire REAL permit data into the unit grid: permit number + fee balance are permit-grain, so join
    // them from the loaded permit rows by the unit's lot(s). Replaces the old fabricated placeholders
    // ("Linked permit" / 0). Permits load lazily on first interaction; if they haven't arrived yet
    // these stay blank (honest) rather than fabricated. `Amount Due` isn't summed in any workbook
    // rollup, so per-unit lot balances are safe.
    if (permitRows.length) {
      const permByLot = new Map<string, any[]>();
      for (const p of permitRows) {
        const lids = (p.lotIds && p.lotIds.length) ? p.lotIds : (p.lotId ? [p.lotId] : []);
        for (const lid of lids) { const arr = permByLot.get(lid) || []; arr.push(p); permByLot.set(lid, arr); }
      }
      for (const r of rows) {
        const seen = new Set<string>(); const perms: any[] = [];
        for (const lid of (r.lotIds || [])) for (const p of (permByLot.get(lid) || [])) if (!seen.has(p.id)) { seen.add(p.id); perms.push(p); }
        if (perms.length) {
          r.permitNum = Array.from(new Set(perms.map(p => p.number).filter(Boolean))).join(', ');
          r.amountDue = perms.reduce((s, p) => s + (Number(p.amountDue) || 0), 0);
        }
      }
    }
    // Wire the two upgrade columns from the lot's parcel: 'Upgrade Package' = GHL Package Selection
    // tier (parcel.unit.sales), 'Construction Upgrades' = the physical upgrades list (parcel-level).
    // Parcel ids ARE lot ids; both feed off the deferred sales/GHL data (blank until it loads).
    const parcelById = new Map(parcels.map(p => [p.id, p]));
    // Per-UNIT GHL sale data (package tier, contract price, escrow) lives on the dock unit rows'
    // joined sale (`unitRows[].sales` — the winning buyer's Sales record), NOT on the parcel's
    // UnitProjection (which has no `sales` field). Join it by unit name. (The earlier
    // `par.unit?.sales` path was always undefined → blank Upgrade Package; fixed here.)
    const saleByUnitName = new Map<string, any>();
    for (const ur of unitRows) if (ur.unitName && ur.sales) saleByUnitName.set(ur.unitName, ur.sales);
    for (const r of rows) {
      const psale = saleByUnitName.get(r.unit);
      if (psale) {
        if (!r.packageSelection && psale.packageSelection) r.packageSelection = psale.packageSelection;
        if (r.salePrice == null && psale.matchedValue != null) r.salePrice = psale.matchedValue;
        if (r.depositUponSigning == null && psale.depositUponSigning != null) r.depositUponSigning = psale.depositUponSigning;
        if (!r.contractExecutionDate && psale.contractExecutionDate) r.contractExecutionDate = psale.contractExecutionDate;
        if (!r.escrowStatus && psale.escrowStatus) r.escrowStatus = psale.escrowStatus;
        if (r.totalDepositedInEscrow == null && psale.totalDepositedInEscrow != null) r.totalDepositedInEscrow = psale.totalDepositedInEscrow;
      }
      // Construction upgrades + incentive categories are parcel-grain (comma-joined on the Parcel).
      for (const lid of (r.lotIds || [])) {
        const par: any = parcelById.get(lid); if (!par) continue;
        if (!r.constructionUpgrades && par.constructionUpgrade) r.constructionUpgrades = par.constructionUpgrade;
        if (!r.financialIncentives && par.financialIncentives) r.financialIncentives = par.financialIncentives;
        if (!r.jobContingency && par.jobContingency) r.jobContingency = par.jobContingency;
        if (!r.additionalContingencies && par.additionalContingencies) r.additionalContingencies = par.additionalContingencies;
        if (r.finalSalesPrice == null && par.priceBreakdown && par.priceBreakdown.finalSalesPrice != null) r.finalSalesPrice = par.priceBreakdown.finalSalesPrice;
      }
    }
    setExporting(`Building workbook · ${rows.length.toLocaleString('en-US')} units…`);
    try {
      // Lazy-load the adapter; ExcelJS itself loads from CDN inside it (same
      // pattern as the live extension) so the writer stays out of the bundle.
      // The view payload appends the Current View (criteria incl. ≠ exclusions) +
      // Distribution / Split Tree sheets after the canonical executive workbook.
      const { buildWorkbookBytes } = await import('./export/buildWorkbook');
      const bytes = await buildWorkbookBytes(rows, {
        lens: LENSES[lens].label,
        date: new Date().toLocaleDateString('en-US'),
        scope: searchScopeId ? `1 searched lot · ${selected?.name ?? ''}` : filterCountLabel(),
      }, {
        criteria: searchScopeId ? `Searched lot: ${selected?.name ?? ''}` : describeFilters(filters, fieldNames),
        parcels: scopeParcels,
        primary: LENSES[lens],
        splits: effSplits,
      });
      const blob = new Blob([bytes as BlobPart], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      // Filename mirrors the map PDF's convention (LFE-Site-Map-{lens}-{date}); a filtered view
      // says so, so a downloaded workbook can never masquerade as the full community.
      const filtered = searchScopeId
        ? `-lot-${(selected?.name ?? searchScopeId).replace(/[^a-z0-9]+/gi, '-')}`
        : (visible.length === parcels.length ? '' : `-filtered-${visible.length}-lots`);
      const base = `LFE-Site-Map-${LENSES[lens].label.replace(/[^a-z0-9]+/gi, '-')}${filtered}-${new Date().toISOString().slice(0, 10)}`;
      a.download = `${base}.xlsx`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 4000);
      // Excel button downloads ONLY the styled .xlsx workbook (Eric's call 2026-06-28 — the
      // separate Airtable-import CSV companion was removed; the workbook IS the deliverable).
      setExporting('✓ Workbook downloaded');
    } catch (e) {
      console.error('export failed', e);
      setExporting('Export failed — see console');
    }
    setTimeout(() => setExporting(null), 2600);
  }
  function filterCountLabel() {
    const total = parcels.length;
    return visible.length === total ? `all ${fmt(total)} lots` : `${fmt(visible.length)} of ${fmt(total)} lots (filtered)`;
  }
  async function exportPDF() {
    // Same rail-search scope as the Excel export: a searched lot scopes the whole PDF (locator map +
    // unit table) to that one lot; the guard reverts to the full view once the selection moves off it.
    const searchScopeId = (searchedLotId && selectedId === searchedLotId) ? searchedLotId : null;
    const scopeParcels = searchScopeId ? parcels.filter(p => p.id === searchScopeId) : visible;
    const scopeLotIdSet = searchScopeId ? new Set(scopeParcels.map(p => p.id)) : visibleLotIdSet;
    if (!scopeParcels.length) { setExporting('No lots in view to export.'); setTimeout(() => setExporting(null), 2600); return; }
    setExporting('Building PDF…');
    try {
      // Map dashboard page + the UNITS grid for the visible lots (same engine as the dock PDF).
      const [{ buildDockPdf }, { makeUnitsConfig }, { buildDynamicUnitColumns }] = await Promise.all([
        import('./export/buildDockPdf'), import('./panels/dock/configs/units'), import('./export/dynamicColumns'),
      ]);
      const unitTableRows = unitRows.filter(r => (r.lotId && scopeLotIdSet.has(r.lotId)) || r.lotIds.some(id => scopeLotIdSet.has(id)));
      // Dynamic columns: Block-Lot, Model, the colour-by, then split-by, then the filter fields that
      // VARY across the view, then Sale & Value — the variables actually in play, in the same priority
      // as the page-1 distribution. Single-value filters (already named in the header) are dropped so
      // the remaining columns widen. exportRows is passed so the builder can test what varies.
      // Lot-level attributes (sidewalk/phase/land-use/concurrency) live on the Parcel, so enrich
      // each unit row from its lot before export.
      const parcelById = new Map(parcels.map(p => [p.id, p]));
      // The colour-by lens, then the split-by lens(es), drive the grid's nested GROUPING — the SAME
      // effective stack and categorisation (LENSES[id].cat) the page-1 "coloured by X · split by Y"
      // distribution uses, so the grouped grid mirrors the chart exactly (Permit status -> Model).
      // buildOrder is a gradient (no meaningful bands) so it never groups; capped at 3 levels.
      const groupLenses: LensId[] = lens === 'buildOrder'
        ? []
        : [lens, ...splits.filter(s => s !== lens && s !== 'buildOrder')].slice(0, 3);
      const exportRows = unitTableRows.map(u => {
        const p = (u.lotId && parcelById.get(u.lotId)) || u.lotIds.map(id => parcelById.get(id)).find(Boolean) || null;
        const base: any = p ? { ...u, sidewalk: p.sidewalk, lakeview: p.lakeview, subPhase: p.subPhase, masterPhase: p.masterPhase, landUseCategory: p.landUseCategory, concurrency: p.unit?.concurrency || '' } : { ...u };
        groupLenses.forEach((gl, i) => { base['__g' + i] = p ? LENSES[gl].cat(p) : '—'; });
        return base;
      });
      const groupLevels = groupLenses.map((gl, i) => ({ label: LENSES[gl].label, keyOf: (r: any) => r['__g' + i] || '—', order: LENSES[gl].order }));
      const dynCols = buildDynamicUnitColumns(lens, splits, filters, exportRows, groupLenses);
      // Stamp the filter scope on the table pages too (the map page already shows the criteria
      // chips). KPI cards here ARE the table scope, so buildDockPdf omits the divergence caveat.
      // Scope the page-1 locator map to the searched lot too (it highlights that lot over the full
      // plat as ghost context), so the map and the unit table agree.
      const mapExport = searchScopeId
        ? { ...dockMapExport, visible: scopeParcels, scope: `1 searched lot (${selected?.name ?? ''})`,
            criteria: `Searched lot: ${selected?.name ?? ''}`, totalSF: scopeParcels.reduce((s, p) => s + (p.lotSizeSF || 0), 0) }
        : dockMapExport;
      const filtered = visible.length !== parcels.length;
      const provenance = searchScopeId
        ? `Export scoped to the searched lot: ${selected?.name ?? ''}`
        : (filtered
          ? `View filtered to ${fmt(visible.length)} of ${fmt(parcels.length)} lots${dockMapExport.criteria ? ' · ' + dockMapExport.criteria : ''}`
          : undefined);
      const bytes = buildDockPdf({ tab: 'Units', rows: exportRows, config: makeUnitsConfig(), lotCount: scopeParcels.length,
        date: new Date().toLocaleDateString('en-US'), map: mapExport, provenance, columns: dynCols,
        groupLevels: groupLevels.length ? groupLevels : undefined, measure: exportMeasure });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      const pdfScope = searchScopeId
        ? `-lot-${(selected?.name ?? searchScopeId).replace(/[^a-z0-9]+/gi, '-')}`
        : (visible.length === parcels.length ? '' : `-filtered-${visible.length}-lots`);
      a.download = `LFE-Site-Map-${LENSES[lens].label.replace(/[^a-z0-9]+/gi, '-')}${pdfScope}-${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 4000);
      setExporting('✓ PDF downloaded');
    } catch (e) { console.error('pdf failed', e); setExporting('PDF failed — see console'); }
    setTimeout(() => setExporting(null), 2600);
  }
  const sfRange = useMemo(() => {
    const sizes = visible.map(p => p.lotSizeSF).filter((n): n is number => n != null);
    return sizes.length ? [Math.min(...sizes), Math.max(...sizes)] : null;
  }, [visible]);
  // Scope the detail panel's inspection/unit lookups once per selection, not on every
  // app re-render (inspectionRows can be thousands of rows; this was scanning inline in JSX).
  const selectedInspRows = useMemo(
    () => selected ? inspectionRows.filter(r => r.lotId === selected.id || r.lotIds.includes(selected.id)) : [],
    [inspectionRows, selected]);
  const selectedUnitRow = useMemo(
    () => selected ? (unitRows.find(r => r.lotId === selected.id || r.lotIds.includes(selected.id)) || null) : null,
    [unitRows, selected]);
  const selectedUpdates = useMemo(
    () => selected ? lotUpdateRows.filter(r => r.lotId === selected.id || r.lotIds.includes(selected.id)) : [],
    [lotUpdateRows, selected]);
  const selectedChangeLog = useMemo(
    () => selected ? changeLogRows.filter(r => r.lotId === selected.id || r.lotIds.includes(selected.id)) : [],
    [changeLogRows, selected]);
  const selectedPunch = useMemo(
    () => selected ? punchRows.filter(r => r.lotId === selected.id || r.lotIds.includes(selected.id)) : [],
    [punchRows, selected]);

  if (loading) return <Skeleton />;
  if (!parcels.length) return <Splash text="No drawable lots found (check Map Polygon JSON)." />;

  return (
    // key={dark} remounts the themed tree on toggle: inline T.* colors live inside React.memo'd
    // children (MapCanvas/Dock/DataGrid) whose props don't change on a theme switch — without the
    // remount they keep painting the previous theme. Toggles are rare; the rebuild cost is fine.
    <div className="lfe" key={dark ? 'dark' : 'light'} style={{ display: 'flex', flexDirection: 'column', height: '100%', maxHeight: '100%', minHeight: 0, overflow: 'hidden' }}>
    {/* Side-column tracks shrink by uiScale to match their zoomed contents (track px are OUTSIDE
        the zoom boundary) — the freed width goes to the 1fr map column. */}
    <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: present ? '1fr' : `${Math.round(330 * uiScale)}px 1fr ${Math.round(detailWidth * uiScale)}px`, gridTemplateRows: 'minmax(0, 1fr)', overflow: 'hidden' }}>
      {!present && <div style={{ ...chromeZoom, minWidth: 0, minHeight: 0, overflow: 'hidden' }}>
        <LeftRail lens={lens} setLens={setLens} splits={splits} setSplits={setSplits} colOrder={colOrder} setColOrder={setColOrder} query={query} setQuery={setQuery} onSearchSubmit={runSearch}
        parcels={parcels} visible={visible} filters={filters} setFilters={setFilters}
        onSelectRow={searchSelect} fieldNames={fieldNames} missingFields={missingFields} />
      </div>}

      <div style={{ position: 'relative', minWidth: 0, minHeight: 0, overflow: 'hidden' }}>
        <MapCanvas parcels={parcels} lens={lens} splitLenses={splits} hoveredLotIds={hoveredLotIds} pinnedLotIds={pinnedLots} selectedId={selectedId} filteredOut={filteredOut} isolated={isolated}
          flyToId={flyToId} flyNonce={flyNonce} fitNonce={fitNonce} tilt={tilt} cb={cb} showMarkers={showMarkers} hovered={hovered} hoveredPath={hoveredPath} hoveredLotId={hoveredLotId} present={present}
          legendVisible={!!(selected && showDetail)} uiScale={uiScale} onSelect={toggleSelect} onToggleIsolate={toggleIsolate} />
        {/* Top-centered overlay: the toolbar pill, then the active-criteria bar stacked beneath it.
            One column stack (not two hardcoded `top` values) so the filter chips always sit BELOW
            the toolbar no matter how tall it wraps — they used to collide on narrow viewports.
            chromeZoom is safe here: these overlays are plain DOM siblings of MapCanvas (absolutely
            positioned over it), NOT inside the Leaflet-managed container. */}
        <div style={{ ...chromeZoom, position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)', zIndex: 500,
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, maxWidth: 'calc(100% - 24px)' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 6, background: T.paper,
            border: `1px solid ${T.line}`, borderRadius: 10, padding: '6px 8px', boxShadow: T.shadow }}>
            <TBtn on={showMarkers} onClick={() => setShowMarkers(m => !m)} title="Toggle critical-lot markers">● Markers</TBtn>
            <TBtn onClick={() => setPaletteOpen(true)} title="Command palette — jump to a lot or lens (⌘K)">⌘K</TBtn>
            <TBtn on={tilt} onClick={() => setTilt(t => !t)} title="2.5D tilt view">30°</TBtn>
            <TBtn onClick={() => setFitNonce(n => n + 1)} title="Fit all lots in view">Fit</TBtn>
            <TBtn on={present} onClick={() => setPresent(p => !p)} title="Presentation mode — hide side panels">{present ? '✕ Exit' : 'Present'}</TBtn>
            <TBtn on={cb} onClick={() => setCb(v => !v)} title="Colour-blind-safe palette">◐ CB</TBtn>
            <TBtn on={dark} onClick={() => setDark(v => !v)} title="Dark ops theme">{dark ? '☀' : '☾'}</TBtn>
            <TBtn onClick={exportXLSX} title="Download an Excel workbook of the current view">⤓ Excel</TBtn>
            <TBtn onClick={exportPDF} title="Download a one-page PDF dashboard of the current view">⤓ PDF</TBtn>
            {exporting && <span className="num" style={{ alignSelf: 'center', fontSize: 11, color: T.accent, padding: '0 4px', whiteSpace: 'nowrap' }}>{exporting}</span>}
            {sfRange && <span className="num mono" style={{ alignSelf: 'center', fontSize: 11, color: T.inkSoft, padding: '0 4px', whiteSpace: 'nowrap' }}>Lot SF {fmt(Math.round(sfRange[0]))} – {fmt(Math.round(sfRange[1]))} SF</span>}
          </div>
          {/* Unified active-criteria bar — every narrowing mechanism, visible + clearable from the map */}
          <CriteriaBar filters={filters} setFilters={setFilters} isolated={isolated}
            onClearIsolation={() => toggleIsolate('__clear__')} fieldNames={fieldNames} />
        </div>
        <div title={`Build ${BUILD}`} style={{ ...chromeZoom, position: 'absolute', left: 12, bottom: 44, zIndex: 500, background: T.paper, border: `1px solid ${T.line}`,
          borderRadius: 9, padding: '5px 10px', fontSize: 11, color: T.inkSoft, boxShadow: T.shadow }} className="num mono">
          Coloring by <b style={{ color: T.ink }}>{lensLabel(LENSES[lens], fieldNames)}</b> · {fmt(visible.length)} of {fmt(parcels.length)} lots{filterCount(filters) > 0 ? ` · ${filterCount(filters)} filters` : ''}
          {syncedAt?.sales != null && <span title="Most recent GHL sales sync reflected in this data (max Last GHL Sync At)"> · Sales synced {new Date(syncedAt.sales).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>}
        </div>
        {visible.length === 0 && (
          <div style={{ ...chromeZoom, position: 'absolute', inset: 0, zIndex: 480, display: 'grid', placeItems: 'center', pointerEvents: 'none' }}>
            <div style={{ pointerEvents: 'auto', textAlign: 'center', background: T.paper, border: `1px solid ${T.line}`, borderRadius: 12, padding: '20px 24px', boxShadow: T.shadow }}>
              <div className="head" style={{ fontSize: 16, color: T.ink }}>No lots match these filters</div>
              <div style={{ fontSize: 12.5, color: T.inkSoft, marginTop: 4 }}>{filterCount(filters)} filter{filterCount(filters) === 1 ? '' : 's'} active</div>
              <button onClick={() => setFilters(emptyFilters())} className="lfe-press"
                style={{ marginTop: 12, fontSize: 12.5, fontWeight: 600, padding: '7px 14px', borderRadius: 8, cursor: 'pointer', border: 'none', background: T.accent, color: T.onAccent }}>Clear all filters</button>
            </div>
          </div>
        )}
      </div>

      {!present && <div style={{ ...chromeZoom, position: 'relative', background: T.paper, borderLeft: `1px solid ${T.line}`, minWidth: 0 }}>
        <DetailResizer width={detailWidth} onResize={setDetailWidth} scale={uiScale} />
        {selected && showDetail
          ? <Suspense fallback={<div style={{ flex: 1, background: T.paper }} />}>
              <DetailPanel parcel={selected} index={selIndex < 0 ? 0 : selIndex} total={visible.length}
                inspectionRows={selectedInspRows}
                unitRow={selectedUnitRow}
                updates={selectedUpdates}
                changeLog={selectedChangeLog}
                punch={selectedPunch}
                onNav={nav} onClose={() => { setShowDetail(false); setSelectedId(null); }} />
            </Suspense>
          : <AnalyticsPanel parcels={visible} facetParcels={facetParcels} lens={lens} splits={splits} setSplits={setSplits} colOrder={colOrder} setColOrder={setColOrder} totalLots={parcels.length} isolated={isolated} onToggleIsolate={toggleIsolate} onSelectLot={selectAndFly} onZoomLot={flyTo} onHoverCat={setHovered} onHoverPath={setHoveredPath} onHoverLots={setHoveredLotIds} onPinLots={setPinnedLots} onMeasureKey={setExportMeasure} cb={cb} unitRows={visibleUnitRows} onHoverLot={setHoveredLotId} filters={filters} setFilters={setFilters} />}
      </div>}

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} parcels={parcels} onSelectLot={selectAndFly} onSetLens={setLens} />
    </div>
    {/* Dock wrapper carries the chrome zoom (Dock keeps its own logical height; the wrapper's
        auto height = height × uiScale, so the 1fr map row above gains the difference). */}
    {!present && <div style={{ ...chromeZoom, flex: '0 0 auto' }}>
      <Dock data={dockData} visibleLotIds={visibleLotIdSet} ctx={gridCtx} activeNoPermit={activeNoPermit}
      open={dockOpen} height={dockHeight} onResize={setDockHeight} onToggle={toggleDock} mapExport={dockMapExport} uiScale={uiScale} />
    </div>}
    </div>
  );
}

// Toolbar toggle — style now lives in ui/primitives Btn variant="solid" (pixel-identical migration).
const TBtn = ({ children, onClick, on, title }: { children: React.ReactNode; onClick: () => void; on?: boolean; title?: string }) => (
  <Btn variant="solid" onClick={onClick} title={title} aria-label={title} on={on}>{children}</Btn>
);

/** Drag handle on the LEFT edge of the detail panel — drag left to widen (mirrors the dock's grip).
 *  `scale` = the chrome zoom on the panel: pointer coords are viewport px, width is logical px,
 *  so the drag delta is divided by scale to keep the handle tracking the cursor 1:1. */
function DetailResizer({ width, onResize, scale = 1 }: { width: number; onResize: (w: number) => void; scale?: number }) {
  const [drag, setDrag] = useState(false);
  const onDown = (e: React.PointerEvent) => {
    e.preventDefault(); setDrag(true);
    const startX = e.clientX, startW = width;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    const move = (ev: PointerEvent) => onResize(Math.min(720, Math.max(300, Math.round(startW + (startX - ev.clientX) / scale))));
    const up = () => { setDrag(false); window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  };
  return (
    <div onPointerDown={onDown} title="Drag to resize" role="separator" aria-orientation="vertical"
      style={{ position: 'absolute', left: -3, top: 0, bottom: 0, width: 7, cursor: 'col-resize', zIndex: 600,
        display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 4, height: 42, borderRadius: 4, background: drag ? T.accent : T.line, transition: 'background .15s' }} />
    </div>
  );
}

function Splash({ text }: { text: string }) {
  return <div className="lfe" style={{ display: 'grid', placeItems: 'center', height: '100%' }}>
    <div style={{ textAlign: 'center' }}>
      <div className="head" style={{ fontSize: 20, color: T.accent }}>Lakefront Estates</div>
      <div style={{ fontSize: 13, color: T.inkSoft, marginTop: 6 }}>{text}</div>
    </div></div>;
}

function Skeleton() {
  const bar = (w: string, h = 12, mt = 10) => <div className="lfe-skel" style={{ width: w, height: h, marginTop: mt }} />;
  return (
    <div className="lfe" style={{ display: 'grid', gridTemplateColumns: '330px 1fr 332px', height: '100%', overflow: 'hidden' }}>
      <div style={{ background: T.paper, borderRight: `1px solid ${T.line}`, padding: 0 }}>
        <div style={{ background: T.maroon, padding: '12px 18px' }}>{bar('60%', 10, 0)}{bar('40%', 18, 6)}</div>
        <div style={{ padding: 18 }}>{bar('30%', 10, 0)}{bar('100%', 38, 8)}{bar('40%', 10, 16)}{bar('100%', 38, 8)}
          {bar('90%', 22, 18)}{bar('80%', 22, 8)}{bar('85%', 22, 8)}{[...Array(7)].map((_, i) => <div key={i}>{bar('100%', 30, 8)}</div>)}</div>
      </div>
      <div style={{ position: 'relative', background: T.sand2 }}>
        <div className="lfe-skel" style={{ position: 'absolute', inset: 16, borderRadius: 12 }} />
      </div>
      <div style={{ background: T.paper, borderLeft: `1px solid ${T.line}`, padding: 18 }}>
        {bar('50%', 16, 0)}{bar('70%', 11, 8)}{[...Array(8)].map((_, i) => <div key={i}>{bar('100%', 26, 12)}</div>)}
      </div>
    </div>
  );
}
